import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { format, addYears, isValid } from 'date-fns';

import { Modal, ModalBody, ModalFooter, ModalHeader } from 'react-bootstrap';
import {
  Briefcase,
  CalendarDays,
  CheckCircle2,
  Flag,
  Globe,
  Lock,
  Pencil,
} from 'lucide-react';

import { TODAY } from 'constants/constants';

import Markdown from 'components/Shared/Markdown';
import MarkdownField from 'components/Shared/MarkdownField/MarkdownField';
import DayRateForm from 'components/Shared/DayRateForm/DayRateForm';
import DualLocationDropdown from 'components/DualLocationDropdown/DualLocationDropdown';
import DatePicker from 'components/Shared/DatePicker';
import Loader from 'components/Loader/Loader';

import { useDispatch } from 'react-redux';
import { update as updateUser } from 'redux/modules/auth';
import useAuth from 'hooks/useAuth';
import { trackEvent } from 'helpers/Analytics';
import {
  formatJoined,
  formatYearsOfExperience,
  getYearsOfExperience,
  hasProjects,
} from 'helpers/portfolioLayout';

import styles from './Bio.module.scss';
import hocStyles from '../../hoc/hoc.module.scss';

const {
  panel,
  headerContainer,
  header,
  editBtn,
  editWrp,
  loader,
  saveBtn,
  cancelBtn,
  tick,
} = hocStyles;

const BIO_MAX_LENGTH = 3000;

// Starting points for the bio. The first sentence shows under the name, so
// each opens with role and location. Roles, education and certifications
// have their own sections, so the bio doesn't repeat them. Replace anything
// in [brackets].
const bioTemplates = {
  Designer: [
    'I’m a [brand / UX / product] designer in [city]. I help [type of client, e.g. early-stage startups] [what you help them do, e.g. launch with a brand that looks established from day one].',
    'Most of my work is [two or three things you do most, e.g. brand identities, design systems and marketing sites]. I’ve worked with [sectors or a notable client or two], so I’m used to [something those clients care about, e.g. tight launch dates].',
    'I design in [your main tools]. I usually [how you like to work, e.g. start with a short discovery call, then share work in progress twice a week].',
    'Tell me what you’re building and when it needs to be ready.',
  ].join('\n\n'),
  Developer: [
    'I’m a [front-end / full-stack / mobile] developer in [city]. I build [what you build, e.g. fast, accessible web apps] for [type of client, e.g. SaaS teams and agencies].',
    'My main stack is [languages and frameworks]. Recent work includes [one or two projects, a line each].',
    'I’m comfortable [how you work, e.g. joining a team’s sprints or owning a build end to end]. You get [what clients get, e.g. tested, documented code that’s easy to hand over].',
    'Send me the brief and your timeline, and I’ll tell you how I’d approach it.',
  ].join('\n\n'),
  Writer: [
    'I’m a [copywriter / content writer / editor] in [city]. I write [what you write, e.g. website copy, articles and email] for [type of client].',
    'I’ve written for [sectors or brands], and I’m good at [what you’re known for, e.g. turning technical detail into plain English]. [A result you can point to.]',
    'I usually work from [your process, e.g. a short brief, one draft and two rounds of edits].',
    'Tell me who you’re writing for and what you want them to do.',
  ].join('\n\n'),
  'Music and audio': [
    'I’m a [composer / producer / sound engineer] in [city]. I make [what you make, e.g. original music for ads, games and podcasts].',
    'My sound is [two or three words, e.g. warm, cinematic and electronic]. I’ve worked on [notable projects, artists or brands], and I deliver [what clients get, e.g. stems, mixes and masters ready for broadcast].',
    'I work from [your setup, e.g. my own treated studio] and [your turnaround, e.g. share a first draft within a week].',
    'Tell me about the project, and send a reference track if you have one.',
  ].join('\n\n'),
  Filmmaker: [
    'I’m a [director / videographer / editor] in [city]. I make [what you make, e.g. brand films and social content] for [type of client].',
    'I can take a project from [your scope, e.g. script and shoot through to edit and color]. Recent work includes [one or two projects or clients].',
    'I shoot on [your kit] and travel to [where you’ll work]. Before we book a shoot, I [your process, e.g. send a short treatment].',
    'Send me the brief, your budget range and the date you need it by.',
  ].join('\n\n'),
  General: [
    'I’m a [your role] in [city]. I help [type of client] with [what you do most].',
    'I’ve worked with [sectors or notable clients] on [kinds of project]. Clients come back for [what you’re known for, e.g. clear updates and work that’s ready on time].',
    'I usually [how you like to work].',
    'Tell me what you’re working on and when you need it.',
  ].join('\n\n'),
};

const Bio = ({
  showMore,
  showShowMoreButton,
  user,
  isOwn,
  onShowMore,
  onReport,
  sentReport,
  onEmailActivate,
  projectCount,
}) => {
  const dispatch = useDispatch();
  const { lastUpdated, updating, updated } = useAuth();

  const [date, setDate] = useState(new Date(user.available_at || TODAY));
  const [editing, setEditing] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  // The guide unmounts with the dialog, so forget it was open.
  useEffect(() => {
    if (!editing) {
      setGuideOpen(false);
    }
  }, [editing]);
  const [editBio, setEditBio] = useState(user.bio || '');
  const [editDayRate, setEditDayRate] = useState(user.day_rate || 0);
  const [editDayRateCurrency, setEditDayRateCurrency] = useState(
    user.day_rate_currency || 'usd'
  );
  const [editAvailability, setEditAvailability] = useState(
    user.available_at || ''
  );
  const [editLocation, setEditLocation] = useState({
    selected: false,
    text: user.location.text || '',
    country: user.location.country || '',
    city: user.location.city || '',
    country_code: user.location.country_code || '',
  });

  // Update local state when user prop changes (e.g., after resume upload)
  useEffect(() => {
    setDate(new Date(user.available_at || TODAY));
    setEditBio(user.bio || '');
    setEditDayRate(user.day_rate || 0);
    setEditDayRateCurrency(user.day_rate_currency || 'usd');
    setEditAvailability(user.available_at || '');
    setEditLocation({
      selected: false,
      text: user.location.text || '',
      country: user.location.country || '',
      city: user.location.city || '',
      country_code: user.location.country_code || '',
      ...user.location,
    });
  }, [user]);

  const handleLocationChange = (location) => {
    setEditLocation({
      ...editLocation,
      ...location,
      selected: true,
    });
  };

  const handleEdit = (editMode) => {
    const updates = {};

    if (editBio !== user.bio) {
      updates.bio = editBio;
    }

    const locationChanged =
      editLocation.city !== user.location.city ||
      editLocation.country !== user.location.country ||
      editLocation.country_code !== user.location.country_code;

    if (locationChanged) {
      updates.location = {
        city: editLocation.city,
        country: editLocation.country,
        country_code: editLocation.country_code,
        method: 'html5',
      };
    }

    if (editDayRate !== user?.day_rate) {
      if (editDayRate / 100 > 3000) {
        updates.day_rate = 3000 * 100;
      } else if (editDayRate == 0 || editDayRate == '') {
        updates.day_rate = 250 * 100; // Just detault to 250 if they don't enter anything
      } else {
        updates.day_rate = editDayRate;
      }
    }

    if (editDayRateCurrency !== user.day_rate_currency) {
      updates.day_rate_currency = editDayRateCurrency;
    }

    if (editAvailability !== user.available_at) {
      updates.available_at = editAvailability;
    }

    if (Object.keys(updates).length > 0) {
      dispatch(updateUser(user, updates, false, [], '', editMode)).then(() => {
        if (updates.bio) trackEvent('freelancer-updates-bio');
        if (updates.location) trackEvent('freelancer-updates-location');
        setEditing(false);
      });
    } else {
      setEditing(false);
    }
  };

  const instanceIsUpdating = useMemo(
    () => lastUpdated === 'contentEditMode',
    [lastUpdated]
  );

  const successTick = !updating &&
    instanceIsUpdating &&
    updated &&
    !editing && <CheckCircle2 className={tick} size={20} aria-label="Saved" />;

  const showBio =
    user.bio.length > 0 && (user.activated || isOwn) ? true : false;

  const collapsed = !showMore && !editing && showShowMoreButton;
  // Stand-ins for experts with little else to show: years of work history when
  // there are no projects, and the join date when there are no reviews.
  const experienceText = hasProjects(user, projectCount)
    ? ''
    : formatYearsOfExperience(getYearsOfExperience(user.workexperience));
  const joinedText =
    (user.rating?.count || 0) > 0 ? '' : formatJoined(user.joined);
  const facts = (experienceText || joinedText) && (
    <ul className={styles.facts}>
      {experienceText && (
        <li>
          <Briefcase size={16} strokeWidth={2} aria-hidden="true" />
          {experienceText}
        </li>
      )}
      {joinedText && (
        <li>
          <CalendarDays size={16} strokeWidth={2} aria-hidden="true" />
          {joinedText}
        </li>
      )}
    </ul>
  );

  // Clients only see About when there is a bio or a fact to read.
  if (!isOwn && !showBio && !facts) {
    return null;
  }

  return (
    <section id="portfolio-bio" className={`${panel} ${styles.about}`}>
      <div className={headerContainer}>
        <h2 className={header}>About</h2>
        {user && isOwn && (
          <div className={editWrp}>
            {successTick}
            {instanceIsUpdating && updating ? (
              <Loader loading className={loader} />
            ) : (
              <button
                id="edit-bio"
                className={editBtn}
                onClick={() =>
                  user.email_verified ? setEditing(true) : onEmailActivate()
                }
                type="button"
                aria-label="Edit about"
              >
                <Pencil size={14} strokeWidth={2} aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
      {isOwn && !editing && (
        <div className={styles.websitePrompt}>
          <Globe size={16} strokeWidth={2} aria-hidden="true" />
          <span>
            {user.pro
              ? 'Your Twine Pro plan includes a professional one-page website.'
              : 'Get a professional one-page website from your portfolio.'}{' '}
            <a href="https://www.twine.net/freelance/one-page-website">
              Pick a template
            </a>
          </span>
        </div>
      )}
      {showBio && (
        <div
          id="expandable_about"
          className={`${styles.bio} ${collapsed ? styles.collapsed : ''}`}
        >
          <Markdown text={user.bio} />
          {/* Inside the bio so a long bio's "See more" fade covers it too. */}
          {facts}
        </div>
      )}
      {isOwn && !user.bio && !editing && (
        <p className={styles.emptyBio}>
          A short bio helps clients decide.{' '}
          <button
            type="button"
            className={styles.link}
            onClick={() =>
              user.email_verified ? setEditing(true) : onEmailActivate()
            }
          >
            Add a few lines
          </button>
        </p>
      )}
      {!showBio && facts}
      {((!editing && showShowMoreButton) || !isOwn) && (
        <div className={styles.footer}>
          {!editing && showShowMoreButton ? (
            <button
              type="button"
              className={styles.link}
              onClick={onShowMore}
              aria-expanded={!!showMore}
            >
              See {showMore ? 'less' : 'more'}
            </button>
          ) : (
            <span />
          )}
          {!isOwn && (
            <button
              type="button"
              className={styles.report}
              onClick={onReport}
              disabled={sentReport}
            >
              <Flag size={12} strokeWidth={2} aria-hidden="true" />
              {sentReport ? 'Reported' : 'Report'}
            </button>
          )}
        </div>
      )}
      <Modal
        show={editing}
        onHide={() => {
          handleEdit('contentEditMode');
        }}
        dialogClassName={styles.dialog}
        // The formatting guide sits outside the dialog: let it take focus,
        // and let Esc close only the guide.
        enforceFocus={!guideOpen}
        keyboard={!guideOpen}
      >
        <ModalHeader closeButton className={styles.dialogHeader}>
          <h2 className={styles.dialogTitle}>Edit about</h2>
          <p className={styles.dialogSubtitle}>
            Shown on your portfolio. The first sentence appears under your name.
          </p>
        </ModalHeader>
        <ModalBody bsClass={styles.dialogBody}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="bio-availability">
              Next availability
            </label>
            <div className={styles.datePickerContainer}>
              <DatePicker
                id="bio-availability"
                className={`form-control ${styles.input}`}
                isClearable={false}
                dateFormat="do MMMM"
                minDate={TODAY}
                maxDate={addYears(TODAY, 1)}
                onChange={(selectedDate) => {
                  if (isValid(selectedDate)) {
                    setDate(selectedDate);
                    setEditAvailability(
                      format(selectedDate, 'yyyy-MM-dd HH:mm:ss')
                    );
                  }
                }}
                placeholderText="Select availability"
                selected={date}
              />
            </div>
            <span className={styles.help}>
              If you’re available now, use today’s date.
            </span>
          </div>
          <div className={styles.field}>
            <div className={styles.locationContainer}>
              <DualLocationDropdown
                defaultCountry={editLocation.country}
                defaultCity={editLocation.city}
                onChangePlace={handleLocationChange}
                layout="vertical"
              />
            </div>
            <span className={styles.help}>
              Used for location-based job alerts.
            </span>
          </div>
          <MarkdownField
            id="bioArea"
            label="Bio"
            labelClassName={styles.label}
            value={editBio}
            onChange={setEditBio}
            placeholder="Tell clients who you are, what you do and what makes you stand out."
            rows={8}
            maxLength={BIO_MAX_LENGTH}
            previewCaption="This is how your bio will look on your portfolio."
            guideSubline="Works in your bio."
            onGuideOpenChange={setGuideOpen}
            help={
              <>
                {editBio.length > BIO_MAX_LENGTH - 500
                  ? `${BIO_MAX_LENGTH - editBio.length} characters left. `
                  : 'Markdown and embeds work here. '}
                <a
                  href="https://www.twine.net/blog/designing-your-portfolio/"
                  target="_blank"
                  title="Designing your portfolio"
                  rel="noreferrer"
                >
                  Read our tips
                </a>
              </>
            }
          />
          <div className={styles.field}>
            <span className={styles.label}>Start from a template</span>
            <div className={styles.templates}>
              {Object.keys(bioTemplates).map((template) => (
                <button
                  type="button"
                  key={template}
                  className={styles.templateChip}
                  onClick={() => setEditBio(bioTemplates[template])}
                >
                  {template}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.divider} />
          <div className={styles.field}>
            <span className={styles.label}>
              Standard day rate{' '}
              <Lock size={13} strokeWidth={2} aria-hidden="true" />
            </span>
            <span className={styles.help}>
              Kept private. We use it to match you to clients.
            </span>
            <DayRateForm
              handleDayRateChange={(rate) => setEditDayRate(rate)}
              handleDayRateCurrencyChange={(currency) =>
                setEditDayRateCurrency(currency)
              }
              dayRate={editDayRate}
              dayRateCurrency={editDayRateCurrency}
            />
          </div>
        </ModalBody>
        <ModalFooter className={styles.dialogFooter}>
          <button
            type="button"
            className={cancelBtn}
            disabled={updating}
            onClick={() => setEditing(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className={saveBtn}
            disabled={updating}
            onClick={() => handleEdit('contentEditMode')}
          >
            Save
          </button>
        </ModalFooter>
      </Modal>
    </section>
  );
};

Bio.propTypes = {
  showMore: PropTypes.bool,
  showShowMoreButton: PropTypes.bool,
  user: PropTypes.object,
  isOwn: PropTypes.bool,
  onShowMore: PropTypes.func,
  onReport: PropTypes.func,
  sentReport: PropTypes.bool,
  onEmailActivate: PropTypes.func,
  projectCount: PropTypes.number,
};

export default Bio;
